import { useState, useEffect, useMemo } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import { ORDER_TYPES } from '@nandosaus/constants';
import { Alert } from '@components/alert';
import { useFeature } from '@hooks/use-feature';
import PropTypes from 'prop-types';

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Radius of the Earth in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

const LocationDistanceAlert = ({ orderType, latitude, longitude, showInfoWhenGeoDisabled, ...rest }) => {
  const { OrderingContextStore, DeliveryStore, LocationStore } = useRootStore();
  const [hasLocationAccess, setHasLocationAccess] = useState(null);
  const [isTooFar, setIsTooFar] = useState(false);

  const currentOrderType = orderType || OrderingContextStore.orderType;

  // Get feature flag with distance thresholds per order type
  const distanceThresholds = useFeature('LocationAlert');

  const { restaurant } = OrderingContextStore;
  const { deliveryAddress } = DeliveryStore;
  const { isLoading } = LocationStore;

  // Get the distance threshold for the current order type (in meters)
  const getDistanceThreshold = () => {
    if (!distanceThresholds || distanceThresholds === false) {
      return null;
    }
    if (currentOrderType === ORDER_TYPES.DELIVERY) {
      return distanceThresholds.delivery;
    }
    if (currentOrderType === ORDER_TYPES.DINE_IN) {
      return distanceThresholds.dinein;
    }
    if (currentOrderType === ORDER_TYPES.PICK_UP) {
      return distanceThresholds.pickup;
    }
    return null;
  };

  const distanceThreshold = getDistanceThreshold();

  // Get target coordinates based on order type - memoized to prevent infinite loops
  const { locationLatitude, locationLongitude } = useMemo(() => {
    if (latitude && longitude) {
      return { locationLatitude: latitude, locationLongitude: longitude };
    }

    if (currentOrderType === ORDER_TYPES.DELIVERY) {
      return {
        locationLatitude: deliveryAddress?.latitude,
        locationLongitude: deliveryAddress?.longitude,
      };
    }

    if (currentOrderType === ORDER_TYPES.DINE_IN || currentOrderType === ORDER_TYPES.PICK_UP) {
      return {
        locationLatitude: restaurant?.latitude,
        locationLongitude: restaurant?.longitude,
      };
    }

    return { locationLatitude: null, locationLongitude: null };
  }, [
    currentOrderType,
    deliveryAddress?.latitude,
    deliveryAddress?.longitude,
    restaurant?.latitude,
    restaurant?.longitude,
    longitude,
    latitude,
  ]);

  useEffect(() => {
    // Don't check location if no valid threshold or coordinates
    if (!distanceThreshold || !locationLatitude || !locationLongitude) {
      return;
    }

    // Check if we already have user location from LocationStore
    const userLat = LocationStore.latitude;
    const userLon = LocationStore.longitude;

    if (userLat && userLon) {
      // Use stored location coordinates
      setHasLocationAccess(true);

      // Calculate distance from target location (in meters)
      const distance = calculateDistance(userLat, userLon, locationLatitude, locationLongitude);

      // Check if user is beyond the threshold distance
      setIsTooFar(distance >= distanceThreshold);
      return;
    }

    // If no stored location, check if geolocation is available in the browser
    if (!navigator.geolocation) {
      setHasLocationAccess(false);
    }
  }, [locationLatitude, locationLongitude, distanceThreshold, LocationStore, currentOrderType]);

  // Hide component if no valid threshold
  if (!distanceThreshold) {
    return null;
  }

  // Hide if no valid coordinates
  if (!locationLatitude || !locationLongitude) {
    return null;
  }

  // Don't render anything while checking location
  if (isLoading) {
    return null;
  }

  // User is close enough to the location
  if (hasLocationAccess && !isTooFar) {
    return null;
  }
  const locationText = currentOrderType === ORDER_TYPES.DELIVERY ? 'address' : 'restaurant';

  // User is too far from the location
  if (hasLocationAccess && isTooFar) {
    return (
      <Alert type="warning" heading="Looks like you're far away" {...rest}>
        Please check your {locationText} before continuing.
      </Alert>
    );
  }

  if (showInfoWhenGeoDisabled) {
    // No location access
    return (
      <Alert type="info" heading="Location access is off" {...rest}>
        Please check your {locationText} before continuing.
      </Alert>
    );
  }

  return null;
};

LocationDistanceAlert.propTypes = {
  orderType: PropTypes.oneOf(Object.values(ORDER_TYPES)),
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  showInfoWhenGeoDisabled: PropTypes.bool,
};

LocationDistanceAlert.defaultProps = {
  orderType: undefined,
  longitude: undefined,
  latitude: undefined,
  showInfoWhenGeoDisabled: false,
};

export { LocationDistanceAlert };
